<template>
  <div class="c-radio-list" :class="classes">
    <CRadioButton v-for="option in allOptions" :disabled="isDisabled(option)" :active="isSelected(option)" @update:active="v => select(v, option)">
      <slot v-if="option === null" name="nullOption">{{ $t('bookingDetail.noMobilitySolution') }}<!-- TODO appropriate label --></slot>
      <slot v-else name="option" :option> {{ option }}</slot>
    </CRadioButton>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import CRadioButton from './CRadioButton.vue';

  type Option = any;

  const props = defineProps<{
    type: 'radio' | 'radiolist' | 'radiobar';
    options: Array<Option>;
    disabledOptions?: Array<Option>;
    disabled?: boolean;
    nullable?: boolean;
  }>();

  const model = defineModel<Option | null>();

  const classes = computed(() => {
    return {
      'c-radio-list-horizontal': props.type === 'radiobar',
      'c-radio-list-vertical': props.type !== 'radiobar',
    };
  });

  const allOptions = computed(() => props.nullable ? [null, ...props.options] : (props.options ?? []));

  function select(active: boolean, option: Option): void {
    if (!active || isSelected(option)) return;
    model.value = option;
  }

  function isSelected(option: Option): boolean {
    return model.value === option;
  }

  function isDisabled(option: Option): boolean {
    return props.disabled || (props.disabledOptions?.includes(option) ?? false);
  }
</script>

<style lang="scss">
  .c-radio-list {
    .c-field-control > & {
      padding: 0;
    }

    &-vertical {
      .c-radio-button {
        width: 100%;
        border-radius: 0;
        padding: $input-padding;
        border: none;
        font-weight: inherit;

        &:not(:last-child) {
          border-bottom: 1px solid $input-border;
        }
      }
    }

    &-horizontal {
      display: flex;
      flex-wrap: wrap;
      gap: .5rem;

      .c-radio-button {
        flex-grow: 1;
        font-weight: inherit;

        .c-validation-failed & {
          border-color: $danger;
        }
      }

      .c-field-control:has(&) {
        border: none;
        background-color: unset;
        box-shadow: none;

        .c-field.c-field-disabled & { // specificity warz
          background-color: unset;

          .c-button-normal-secondary {
            border: none;
          }
        }
      }
    }
  }

  .c-radio-list .c-button.c-button-light-primary:hover {
    background-color: #d5f0ff;
  }
</style>
